/**
 * Tâches 1247 · 1248 (Eric 2026-06-28) — Carte « Tâches quotidiennes internes ».
 *
 * Quatre familles d'incidents « la tâche X ne tourne plus » revenaient sans
 * cesse : ces traitements ne partaient que si un planificateur EXTERNE était
 * branché à la main. Ils tournent maintenant dans le pod, à heure fixe, et
 * cette carte le montre noir sur blanc.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowsClockwise, CheckCircle, Clock, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const DailyJobsCard = ({ initial , awaiting }) => {
  const [jobs, setJobs] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [running, setRunning] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sentinel/daily-jobs`, {
        withCredentials: true,
      });
      setJobs(data.jobs || []);
    } catch (e) {
      if (e.response?.status === 403) setHidden(true);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setJobs(initial.jobs || []);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const runNow = async (job) => {
    setRunning(job);
    try {
      const { data } = await axios.post(
        `${API}/api/sentinel/daily-jobs/run-now`,
        { job },
        { withCredentials: true, timeout: 180000 },
      );
      setJobs(data.jobs || []);
      if (data.run?.ok) toast.success(`${data.run.label} : exécutée`);
      else toast.error(`${data.run?.label} : ${data.run?.error || "échec"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Exécution impossible");
    } finally {
      setRunning(null);
    }
  };

  if (hidden) return null;

  return (
    <section
      className="bg-white rounded-xl border border-zinc-200 p-5 mb-6"
      data-testid="daily-jobs-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <Clock size={20} weight="duotone" /> Tâches quotidiennes internes
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Ces traitements ne partaient que si un planificateur externe était branché à la
            main : quatre familles d&apos;incidents « la tâche ne tourne plus » revenaient sans
            cesse. Ils tournent maintenant dans le pod, à heure fixe.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="daily-jobs-refresh-btn">
          <ArrowsClockwise size={16} className="mr-2" /> Rafraîchir
        </Button>
      </div>

      {!jobs ? (
        <p className="text-sm text-zinc-500">Chargement…</p>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="daily-jobs-list">
          {jobs.map((j) => (
            <li
              key={j.job}
              className="py-2 flex items-start justify-between gap-3 flex-wrap"
              data-testid={`daily-job-${j.job}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-900 flex items-center gap-2">
                  {j.last_ok === false ? (
                    <WarningCircle size={16} className="text-red-600" weight="fill" />
                  ) : j.ran_today ? (
                    <CheckCircle size={16} className="text-teal-600" weight="fill" />
                  ) : (
                    <Clock size={16} className="text-zinc-400" />
                  )}
                  {j.label}
                  <span className="text-xs font-normal text-zinc-500">{j.scheduled_local}</span>
                </div>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {j.last_run_at
                    ? `Dernière exécution : ${new Date(j.last_run_at).toLocaleString("fr-CA")} (${j.last_source || "interne"})`
                    : "Jamais exécutée dans ce pod"}
                  {" · "}
                  {j.ran_today ? "faite aujourd'hui" : `prochaine : ${j.next_run_local}`}
                </p>
                {j.last_error && (
                  <p className="text-xs text-red-700 mt-0.5" data-testid={`daily-job-error-${j.job}`}>
                    {j.last_error}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={running === j.job}
                onClick={() => runNow(j.job)}
                data-testid={`daily-job-run-${j.job}`}
              >
                {running === j.job ? "En cours…" : "Lancer maintenant"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DailyJobsCard;
